import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import useApi from '../../api';
import NxtbnDrawer from '../../components/drawer';
import { InputField } from '../../components/common';
import ProgressBar from '../../components/purchase/progressBar';
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client";

const GET_STOCK_TRANSFER_RECEIVING = gql`
  query GetStockTransferDetails($id: ID!) {
    stockTransfer(id: $id) {
      dbId
      status
      toWarehouse {
        name
        location
      }
      fromWarehouse {
        name
        location
      }
      items {
        edges {
          node {
            dbId
            id
            quantity
            receivedQuantity
            rejectedQuantity
          }
        }
      }
    }
  }
`;


interface ReceiveTransferProps {
    isOpen: boolean;
    onClose: () => void;
    transferId: number;
    refresh: () => void;
}

function ReceiveTransfer({ isOpen, onClose, transferId, refresh }: ReceiveTransferProps) {
    const [formData, setFormData] = useState<any>();
    const [progress, setProgress] = useState<number>(0);
    const [total, setTotal] = useState<any>({totalAcceptedQuantity: 0, totalRejectedQuantity: 0, totalOrderededQuantity:0})

    const api = useApi();

    const { data, loading, error, refetch } = useQuery(GET_STOCK_TRANSFER_RECEIVING, {
            variables: { id: transferId },
            notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        if (data.stockTransfer) {
            const processedData = {
                items: data.stockTransfer.items.edges.map((item: any) => ({
                    id: item.node.dbId,
                    quantity: item.node.quantity,
                    received_quantity: item.node.receivedQuantity,
                    rejected_quantity: item.node.rejectedQuantity,
                })),
            };
            setFormData(processedData);
            calculateProgress(processedData.items);         
        }
    }, [data]);

    const calculateProgress = (items: any[]) => {
        const totalReceived = items.reduce(
            (sum, item) => sum + (item.received_quantity || 0) + (item.accepted_quantity || 0),
            0
        );
        const totalOrdered = items.reduce((sum, item) => sum + item.quantity, 0);
        setProgress(totalReceived / totalOrdered);
    };

    const calculateTotal = () => {
        const totalAcceptedQuantity = formData?.items.reduce((sum:any, item:any)=>sum+(item.received_quantity || 0), 0)
        const totalRejectedQuantity = formData?.items.reduce((sum:any, item:any)=>sum+(item.rejected_quantity || 0), 0)
        const totalOrderededQuantity = formData?.items.reduce((sum:any, item:any)=>sum+(item.quantity || 0), 0)
        
        setTotal({
            totalAcceptedQuantity: totalAcceptedQuantity, 
            totalRejectedQuantity: totalRejectedQuantity,
            totalOrderededQuantity: totalOrderededQuantity
        })
    }




    const handleAccept = (index: number, value: number) => {
        if(value + formData.items[index].rejected_quantity <= formData.items[index].quantity) {
            const updatedItems = [...formData?.items];
            updatedItems[index].received_quantity = value;
            setFormData((prev:any)=>({...prev, items:updatedItems}));
            calculateProgress(updatedItems);
        }else {
            toast.warn('The accepted quantity exceeds the allowed limit for this order !')
        }
    };

    const handleReject = (index: number, value: number) => {
        if(value + formData.items[index].received_quantity <= formData.items[index].quantity) {
            const updatedItems = [...formData.items];
            updatedItems[index].rejected_quantity = value;
            setFormData((prev:any)=>({...prev, items: updatedItems}));
        }else {
            toast.warn('The rejected quantity exceeds the allowed limit for this order !')
        }
    };

    const handleAcceptAll = (index: number) => {
        const updatedItems = [...formData.items];
        const item = updatedItems[index];
        const maxAcceptable = item.quantity - (item.rejected_quantity || 0);
        updatedItems[index].received_quantity = maxAcceptable;
        setFormData((prev:any)=>({...prev, items: updatedItems}));
        calculateProgress(updatedItems);
    };

    const handleRejectAll = (index: number) => {
        const updatedItems = [...formData.items];
        const item = updatedItems[index];
        const maxRejectable = item.quantity - (item.received_quantity || 0);
        updatedItems[index].rejected_quantity = maxRejectable;
        setFormData((prev:any)=>({...prev, items: updatedItems}));
    };


    useEffect(()=>{
        calculateTotal()
    }, [formData])

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        api.receiveStockTransfer(transferId, formData).then((response:any)=>{
            toast.success('Inventory received successfully!');
            refresh();
            onClose();

        }).catch((error)=>{
            toast.error('Error receiving inventory!')
        })
        
    };


    return (
        <NxtbnDrawer
            isOpen={isOpen}
            onClose={onClose}
            title="Receive Inventory"
        >
            <form onSubmit={handleSubmit} className="p-6">
                <div className="text-sm text-gray-500 mb-2">
                    Received {total.totalAcceptedQuantity || 0} item of {total.totalOrderededQuantity || 0}
                </div>
                <ProgressBar acceptValue={total.totalAcceptedQuantity} rejectedValue={total.totalRejectedQuantity} max={total.totalOrderededQuantity} className="mb-4" />

                <div className="space-y-4">
                    {formData?.items.map((item:any, index:number) => (
                        <div className="border p-4 rounded-md shadow-sm"  key={index}>
                            <div  className="flex items-center justify-between mb-4">
                                <div className="font-medium text-gray-700 flex-1 mr-4">
                                    {item.product_name}
                                </div>

                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-2">
                                        <InputField
                                            type="number"
                                            label="Accept"
                                            value={item.received_quantity || ''}
                                            onChange={(e) => handleAccept(index, Number(e.target.value))}
                                            className="w-20 border"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleAcceptAll(index)}
                                            className="text-primary-600 text-sm hover:underline"
                                        >
                                            All
                                        </button>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <InputField
                                            type="number"
                                            label="Reject"
                                            value={item.rejected_quantity || ''}
                                            onChange={(e) => handleReject(index, Number(e.target.value))}
                                            className="w-20 border"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleRejectAll(index)}
                                            className="text-primary-600 text-sm hover:underline"
                                        >
                                            All
                                        </button>
                                    </div>
                                </div>
                            </div>
                            

                            <div>
                                <ProgressBar acceptValue={item.received_quantity || 0} rejectedValue={item.rejected_quantity || 0} max={item.quantity || 0} className="mt-4" />
                                <p className="text-sm text-gray-500 mb-2">Received {item.received_quantity} item of {item.quantity} </p>
                            </div>

                        </div>
                    ))}
                </div>

                <div className="flex justify-end mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="mr-3 inline-flex justify-center rounded-md border px-4 py-2 bg-gray-600 text-base font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="inline-flex justify-center rounded-md border px-4 py-2 bg-primary-600 text-base font-medium text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                    >
                        Submit
                    </button>
                </div>
            </form>
        </NxtbnDrawer>
    );
}

export default ReceiveTransfer;
